import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useFormikContext } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import {
  useGetIntermediateDirectAccountsQuery,
  useUpdateDirectAccountsMutation,
} from "store/apis/bankConnections";
import { BankConnection } from "types/Models/banks";
import { ArrayElement } from "types/utils/ArrayElement";

const DirectManage = ({
  connection,
  isDisabled,
}: {
  connection: BankConnection;
  isDisabled?: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<{
    selectedAccounts: string[];
  }>();

  const entityId = useCurrentEntityId();

  const { data } = useGetIntermediateDirectAccountsQuery(
    {
      entityItemId: connection.entity_item_id,
      entityId: entityId,
    },
    { skip: isDisabled }
  );

  const accounts = isDisabled
    ? connection.bank_accounts.map((acc) => ({
        name: acc.name,
        mask: acc.account_number?.slice(-4) || "",
        balance: acc.available_balance,
        account_id: acc.uuid,
      }))
    : data;

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    account: ArrayElement<NonNullable<typeof accounts>>
  ) =>
    setFieldValue(
      "selectedAccounts",
      e.target.checked
        ? [...values.selectedAccounts, account.name]
        : values.selectedAccounts.filter((id) => id !== account.name)
    );

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {!isDisabled && (
        <p className="t-text-body-sm mb-0">
          Review and update your connected bank accounts to ensure accurate
          bookkeeping.
        </p>
      )}
      {!isDisabled && (
        <Banner
          type="warning"
          description="Unselecting only stops new data from coming in and the already synced data will have to be managed manually."
        />
      )}

      <div className="t-flex t-item-center t-flex-col t-gap-4 t-w-full">
        {accounts?.map((account) => (
          <div className="t-flex t-gap-2 t-text-text-60" key={account.name}>
            <Checkbox
              alignTop
              checked={values.selectedAccounts.includes(account.name)}
              name={`selectedAccounts.${account.name}`}
              onChange={(e) => onChange(e, account)}
              disabled={isDisabled}
            />
            <label
              htmlFor={`selectedAccounts.${account.name}`}
              className="t-flex t-gap-0.5 t-flex-col t-bg-white t-w-full t-rounded-lg t-cursor-pointer"
            >
              <span className="t-text-subtext">
                {account.name} ••• {account.mask}
              </span>
              <span className="t-text-body-sm t-text-text-30">
                Account Balance:{" "}
                <AmountSuperScript amount={Number(account.balance || 0)} />
              </span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DirectManageAccounts = ({
  isOpen,
  close,
  connection,
}: {
  isOpen: boolean;
  close: () => void;
  connection: BankConnection;
}) => {
  const entityId = useCurrentEntityId();
  const { secondary_channel_url } = useCurrentGroupContext();

  const isDisabled = !connection.is_connection_available;

  const { data, isLoading } = useGetIntermediateDirectAccountsQuery(
    {
      entityItemId: connection.entity_item_id,
      entityId: entityId,
    },
    { skip: isDisabled }
  );

  const { successToast } = useToast();
  const [updateDirectAccounts, { isLoading: updating }] =
    useUpdateDirectAccountsMutation();

  const onSubmit = async (values: { selectedAccounts: string[] }) => {
    const updatedAccounts = data?.filter((account) =>
      values.selectedAccounts.includes(account.name)
    );

    if (updatedAccounts) {
      await updateDirectAccounts({
        entityId,
        entityItemId: connection.entity_item_id,
        payload: updatedAccounts,
      }).unwrap();
    }

    close();

    successToast({
      message: "Accounts updated successfully",
    });
  };

  if (isLoading && !isDisabled) {
    return (
      <Modal.Root open={isLoading}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Manage accounts</Modal.Title>
          </Modal.Header>
          <Modal.Body className="t-flex t-justify-center">
            <Loader size="small" />
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  const initallySelectedAccounts = isDisabled
    ? connection.bank_accounts.map((acc) => acc.name)
    : data
        ?.filter(
          ({ account_id }) =>
            account_id &&
            !connection.bank_accounts.find((acc) => acc.uuid === account_id)
              ?.mark_as_manually_added
        )
        .map(({ name }) => name) || [];

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large" asChild>
        <FormikForm
          initialValues={{
            selectedAccounts: initallySelectedAccounts,
          }}
          // @ts-ignore
          onSubmit={onSubmit}
        >
          <Modal.Header>
            <Modal.Title>Manage accounts</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <DirectManage connection={connection} isDisabled={isDisabled} />
          </Modal.Body>
          <Modal.Footer>
            <div className="t-flex t-justify-between">
              <div className="t-flex t-gap-1 t-text-subtext t-text-text-30 t-items-center">
                <span>Need help?</span>
                <ConditionalLink
                  className="t-text-purple"
                  to={`/books/chat/${secondary_channel_url}`}
                  target="_blank"
                >
                  Talk to us
                </ConditionalLink>
              </div>
              <div className="t-flex t-gap-3">
                <Modal.RawClose asChild>
                  <Button>{isDisabled ? "Close" : "Cancel"}</Button>
                </Modal.RawClose>
                {!isDisabled && (
                  <Button isLoading={updating} customType="primary">
                    Confirm
                  </Button>
                )}
              </div>
            </div>
          </Modal.Footer>
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
